// src/components/AddendumsModal.js
//
// v3.5.99.4 — Per-quote Special Terms (Addendums) modal.
//
// Workflow:
//   1. Quote page mounts modal with `open` toggled by an "Add Addendums"
//      button. Passes current `addendums` array (from inputs.addendums) and
//      the loaded `templates` array.
//   2. Rep adds templates from the picker. Each addition seeds the entry
//      via defaultEntry(template) — empty placeholder values, default-on
//      optional clauses enabled.
//   3. Rep fills in placeholder values and toggles optional clauses.
//   4. Rep clicks Save. validateAddendums runs; on errors, modal stays
//      open and highlights the offending fields. On success, parent's
//      onSave is called with the new array and the modal closes.
//   5. Cancel discards all draft changes and closes — parent's addendums
//      state is untouched.
//
// The parent quote page is responsible for its own save validation too
// (defense-in-depth — addendums state might be mutated outside this modal
// by future code paths, or via a stale tab that bypassed the modal's
// validation). The quote save handler runs validateAddendums() and blocks
// if errors exist.

import { useState, useEffect, useMemo } from 'react';
import { defaultEntry, validateAddendums } from '../lib/addendums';

export default function AddendumsModal({ open, onClose, addendums, onSave, templates }) {
  const [draft, setDraft]   = useState([]);
  const [errors, setErrors] = useState([]);

  // Reset working copy whenever the modal opens with a (possibly different)
  // set of incoming addendums. Cancels do NOT mutate the parent state, so
  // re-opening shows the last-saved state of the world, not the last-edited.
  useEffect(() => {
    if (open) {
      setDraft(Array.isArray(addendums) ? JSON.parse(JSON.stringify(addendums)) : []);
      setErrors([]);
    }
  }, [open, addendums]);

  // Build picker options: only templates not already attached.
  const availableTemplates = useMemo(() => {
    const attached = new Set(draft.map(e => e.template_id));
    return (templates || []).filter(t => !attached.has(t.id));
  }, [draft, templates]);

  // Index errors by template_id + placeholder_key for fast lookup in render.
  const errorIndex = useMemo(() => {
    const idx = {};
    for (const e of errors) {
      const key = `${e.template_id}::${e.placeholder_key || '_'}`;
      idx[key] = e;
    }
    return idx;
  }, [errors]);

  if (!open) return null;

  function addTemplate(templateId) {
    const t = templates.find(t => t.id === templateId);
    if (!t) return;
    setDraft(d => [...d, defaultEntry(t)]);
  }

  function removeEntry(idx) {
    setDraft(d => d.filter((_, i) => i !== idx));
    setErrors(es => es.filter(e => e.template_id !== draft[idx]?.template_id));
  }

  function setPlaceholderValue(entryIdx, key, value) {
    setDraft(d => {
      const next = [...d];
      next[entryIdx] = {
        ...next[entryIdx],
        placeholder_values: { ...next[entryIdx].placeholder_values, [key]: value },
      };
      return next;
    });
    // Clear matching error eagerly as the rep types.
    setErrors(es => es.filter(e => !(e.template_id === draft[entryIdx]?.template_id && e.placeholder_key === key)));
  }

  function toggleClause(entryIdx, clauseKey) {
    setDraft(d => {
      const next = [...d];
      const current = Array.isArray(next[entryIdx].enabled_clauses) ? next[entryIdx].enabled_clauses : [];
      next[entryIdx] = {
        ...next[entryIdx],
        enabled_clauses: current.includes(clauseKey)
          ? current.filter(k => k !== clauseKey)
          : [...current, clauseKey],
      };
      return next;
    });
  }

  function handleSave() {
    const errs = validateAddendums(draft, templates);
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    onSave(draft);
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 30, 60, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8, maxWidth: 720, width: '100%',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f1e3c', margin: 0 }}>Special Terms (Addendums)</h2>
            <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Attach addendum templates to this quote. Each will render in the Service Order PDF between the OOS Rate Schedule and Acceptance Terms.</p>
          </div>
          <button onClick={onClose} style={{ padding: '4px 10px', fontSize: 16, background: 'transparent', color: '#9ca3af', border: 'none', cursor: 'pointer', lineHeight: 1 }} title="Close">×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {errors.length > 0 && (
            <div style={{ padding: '8px 12px', marginBottom: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, fontSize: 11, color: '#991b1b' }}>
              <strong>Can't save yet:</strong> {errors.length} placeholder{errors.length === 1 ? '' : 's'} still empty. Fill the highlighted fields below or remove the addendum.
            </div>
          )}

          {/* Attached addendums */}
          {draft.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: '#9ca3af', fontStyle: 'italic', background: '#f9fafb', borderRadius: 6, marginBottom: 16 }}>
              No addendums attached. Add one from the picker below.
            </div>
          ) : (
            draft.map((entry, idx) => {
              const t = templates.find(t => t.id === entry.template_id);
              if (!t) {
                return (
                  <div key={idx} style={{ padding: 12, marginBottom: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, fontSize: 11, color: '#991b1b' }}>
                    Unknown template (deleted?). <button onClick={() => removeEntry(idx)} style={{ marginLeft: 8, fontSize: 10, color: '#991b1b', background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Remove</button>
                  </div>
                );
              }
              return (
                <div key={idx} style={{ padding: 14, marginBottom: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c' }}>{t.name}</div>
                      {t.description && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>{t.description}</div>}
                    </div>
                    <button onClick={() => removeEntry(idx)} style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, background: 'white', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 3, cursor: 'pointer' }}>Remove</button>
                  </div>

                  {/* Placeholders */}
                  {(t.placeholders || []).length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      {(t.placeholders || []).map(ph => {
                        const value = entry.placeholder_values?.[ph.key] || '';
                        const errKey = `${t.id}::${ph.key}`;
                        const hasError = !!errorIndex[errKey];
                        return (
                          <div key={ph.key} style={{ marginBottom: 8 }}>
                            <label style={{ fontSize: 10, fontWeight: 700, color: hasError ? '#991b1b' : '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                              {ph.label || ph.key} *
                            </label>
                            {ph.type === 'multiline_list' ? (
                              <textarea
                                value={value}
                                onChange={e => setPlaceholderValue(idx, ph.key, e.target.value)}
                                placeholder={ph.help || ''}
                                rows={3}
                                style={{ display: 'block', width: '100%', marginTop: 3, padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', lineHeight: 1.5, border: `1px solid ${hasError ? '#fecaca' : '#d1d5db'}`, borderRadius: 3, resize: 'vertical' }}
                              />
                            ) : (
                              <input
                                value={value}
                                onChange={e => setPlaceholderValue(idx, ph.key, e.target.value)}
                                placeholder={ph.help || ''}
                                style={{ display: 'block', width: '100%', marginTop: 3, padding: '6px 10px', fontSize: 12, border: `1px solid ${hasError ? '#fecaca' : '#d1d5db'}`, borderRadius: 3 }}
                              />
                            )}
                            {ph.help && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{ph.help}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Optional clauses */}
                  {(t.optional_clauses || []).length > 0 && (
                    <div style={{ marginTop: 12, padding: '8px 10px', background: '#f8fafc', borderRadius: 4 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Optional Clauses</div>
                      {(t.optional_clauses || []).map(c => {
                        const checked = Array.isArray(entry.enabled_clauses) && entry.enabled_clauses.includes(c.key);
                        return (
                          <label key={c.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: '#374151', marginBottom: 4, cursor: 'pointer' }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleClause(idx, c.key)} style={{ marginTop: 2 }} />
                            <span>{c.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Picker — only show available (un-attached) templates */}
          {availableTemplates.length > 0 ? (
            <div style={{ marginTop: 4, padding: 12, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Add an Addendum</div>
              {availableTemplates.map(t => (
                <div key={t.id} style={{ padding: 8, marginBottom: 6, background: 'white', border: '1px solid #e5e7eb', borderRadius: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0f1e3c' }}>{t.name}</div>
                    {t.description && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>{t.description}</div>}
                  </div>
                  <button onClick={() => addTemplate(t.id)} style={{ padding: '4px 12px', fontSize: 10, fontWeight: 700, background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Add</button>
                </div>
              ))}
            </div>
          ) : (
            draft.length === 0
              ? <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>No addendum templates configured. Ask an admin to add templates via Admin → Addendums.</div>
              : <div style={{ padding: 8, textAlign: 'center', fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>All available templates already attached.</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa', borderRadius: '0 0 8px 8px' }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            {draft.length === 0 ? 'No addendums attached.' : `${draft.length} addendum${draft.length === 1 ? '' : 's'} attached.`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} style={{ padding: '6px 16px', fontSize: 11, fontWeight: 700, background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
